/**
 * WS-3 Uploads (UID-41, docs/design/screens.md §10) — landing page for
 * starting a new CAD upload batch; lists recent staging sessions.
 *
 * The single-file presign step (`presignUpload`) is the FROZEN, already-
 * shipped UID-40 contract (`POST /intake/uploads` → `CadUploadTicket`,
 * `apps/api/openapi/paths/intake.yaml`) — this vertical wires to it
 * directly (see `useUploads.ts`), it is NOT mocked. The batch/"staging
 * session" concept below (grouping several presigned uploads, listing
 * recent sessions, resuming one) has no shipped endpoint on `main` yet —
 * UID-41/UID-42's batch-session BFF work is in progress on a separate,
 * unmerged branch, so THAT part stays behind a typed mock service here.
 */

export type UploadFileStatus = 'queued' | 'uploading' | 'uploaded' | 'failed'

export interface UploadFileState {
  readonly id: string
  readonly fileName: string
  readonly sizeBytes: number
  readonly status: UploadFileStatus
  readonly progress: number
  readonly error?: string
}

/**
 * `'confirmed'` (UID-140) is a CLIENT-SIDE-ONLY member today — the real wire
 * type (`UploadSessionStatus` in `apps/web/src/lib/api/schema.gen.ts`,
 * generated from `apps/api/openapi.yaml`) is still only `"uploading" |
 * "identifying" | "ready"`. `defaultUploadsService.listSessions` therefore
 * can never actually return a `'confirmed'` session today — the
 * "Recently confirmed" section (S5) only renders once the BFF grows a real
 * confirm mutation + status value.
 * TODO(UID-141): add a real `POST /uploads/sessions/{id}/confirm` endpoint
 * that persists this transition server-side.
 */
export type UploadSessionStatus = 'uploading' | 'identifying' | 'ready' | 'confirmed'

export const UPLOAD_SESSION_STATUS_LABEL: Record<UploadSessionStatus, string> = {
  uploading: 'Uploading',
  identifying: 'Identifying parts',
  ready: 'Ready to review',
  confirmed: 'Confirmed',
}

/** Text-carried-status color per `UploadSessionStatus` (accessibility-wcag:
 * status is never color-only — every one of these is always paired with the
 * `UPLOAD_SESSION_STATUS_LABEL` text). CSS custom-property references so the
 * value follows the shared token file (`styles/tokens.css`), not a
 * hand-picked hex. */
export const UPLOAD_SESSION_STATUS_COLOR: Record<UploadSessionStatus, string> = {
  uploading: 'var(--color-text-tertiary)',
  identifying: 'var(--color-info)',
  ready: 'var(--color-success)',
  confirmed: 'var(--color-success)',
}

/**
 * View-model for a batch upload session (UID-41/UID-140). `sessionId`,
 * `name`, `fileCount`, `status`, `createdAt` mirror the REAL, shipped
 * `UploadSessionSummary` wire shape (`GET /uploads/sessions`) 1:1 —
 * `defaultUploadsService` maps those fields straight through.
 *
 * `workspaceId` (UID-298, Slice B of UID-258) is now ALSO real: the BFF
 * persists and echoes it back (`intake.staging_session.workspace_id`,
 * migration 1010). `workspaceName` and every field below that line remain
 * ADDITIVE and OPTIONAL — the Uploads-landing design (UID-140, `Fictiv
 * Platform.dc.html:2933-2952`) needs a display name, a part count, a
 * progress percentage, and thumbnails the real BFF does not return yet.
 * Per README.md §"Uploaded parts / RFQs ↔ projects" and the `sessionCards`
 * data model (`Fictiv Platform.dc.html:9198-9207`), the comp does NOT call
 * for a project label on this in-progress card — only that the assignment
 * carries into the library as a tag, which Slice A/B already deliver. So
 * `workspaceName` staying undefined against the real BFF is not a gap.
 * `defaultUploadsService` leaves every one of these `undefined` rather than
 * fabricating a value; every consumer (`InProgressSessionCard`,
 * `ConfirmedSessionRow`) must degrade gracefully when they're absent. The
 * richer, fully-populated shape is only ever produced by the injectable
 * test/demo seam (`test-fixtures.ts`).
 * TODO(UID-146): persist + return `partCount`/`progressPct`/`thumbnailUrls`
 * once the intake pipeline reports part-level identification progress.
 */
export interface UploadSessionSummary {
  readonly sessionId: string
  readonly name: string
  readonly fileCount: number
  readonly status: UploadSessionStatus
  readonly createdAt: string
  /** Workspace this session is filed under; `null`/absent means "kept as
   * draft (unfiled)". */
  readonly workspaceId?: string | null
  readonly workspaceName?: string
  /** Parts identified so far. Absent when the BFF hasn't reported it yet. */
  readonly partCount?: number
  /** 0-100. Absent when the BFF hasn't reported a real progress percentage
   * — callers fall back to a coarse status-based estimate for the progress
   * bar (see `progressPctFor` in `components/InProgressSessionCard.tsx`),
   * which is a UI approximation, never presented as an exact figure. */
  readonly progressPct?: number
  /** Thumbnail image URLs for the stacked-thumbnail cluster (S4). Absent or
   * empty renders a graceful gray placeholder cluster instead. */
  readonly thumbnailUrls?: readonly string[]
}

/** Mirrors `CadUploadRequest` (`apps/api/openapi.yaml`) — the exact body
 * `POST /intake/uploads` expects. */
export interface PresignUploadRequest {
  readonly filename: string
  readonly contentType: string
  readonly sizeBytes: number
}

/** Mirrors `CadUploadTicket` — the presigned-PUT response the browser
 * replays verbatim against S3. */
export interface PresignUploadTicket {
  readonly uploadId: string
  readonly objectKey: string
  readonly url: string
  readonly method: 'PUT'
  readonly headers: Readonly<Record<string, string>>
  readonly expiresAt: string
  readonly maxBytes: number
}

/**
 * ADR-0135 D1/D4a — one file's presigned PUT as returned by
 * `POST /uploads/sessions`, which mints the object key AND persists it as
 * `staged_file.object_key` in the SAME operation (the D3a one-minter standard).
 * The browser MUST PUT to this ticket; presigning the file again mints a second,
 * different key, and the byte-reading seam that reads the persisted key then
 * finds nothing (ADR-0134 R5).
 *
 * Mirrors `UploadSessionFileTicket` (`apps/api/openapi/schemas/intake.yaml`) with
 * ONE addition made in the web adapter, not the contract: `maxBytes`. On the wire
 * that is a SESSION-level field (`UploadSession.maxBytes`) because it is a server
 * constant, not a per-file property; duplicating it onto up to 200 array elements
 * to spare the client one line would be denormalizing the contract (D4a).
 * `defaultUploadsService.createSession` composes it in, which is what lets these
 * tickets satisfy `putFileToS3`/`assertWithinMaxBytes`'s deliberately narrow
 * structural `S3PutTicket` seam with no change to `s3Upload.ts`.
 */
export interface UploadSessionPutTicket {
  readonly uploadId: string
  readonly filename: string
  readonly objectKey: string
  readonly url: string
  readonly method: 'PUT'
  readonly headers: Readonly<Record<string, string>>
  readonly expiresAt: string
  /** Session-level on the wire; composed here. See the doc comment (D4a). */
  readonly maxBytes: number
}

/**
 * ADR-0135 D1 — what `createSession` resolves with: the session summary every
 * existing consumer already reads, PLUS the presigned tickets it used to throw
 * away.
 *
 * `tickets` is INDEX-ALIGNED with the `files` argument that was passed in
 * (ADR-0135 D4, pinned by an API test). Correlate BY INDEX, never by filename —
 * filenames are not unique within a batch (two dragged folders can each contain
 * `bom.csv`) and the request schema carries no uniqueness constraint.
 */
export interface CreatedUploadSession extends UploadSessionSummary {
  readonly tickets: readonly UploadSessionPutTicket[]
}

export const ACCEPTED_CAD_EXTENSIONS = [
  '.step',
  '.stp',
  '.iges',
  '.igs',
  '.sldprt',
  '.x_t',
  '.stl',
  '.pdf',
  '.csv',
] as const

/**
 * Extensions for the *drawing*
 * picker, distinct from `ACCEPTED_CAD_EXTENSIONS` (which is the 3D CAD-model
 * list and includes formats — .step/.stp/.iges/.igs/.sldprt/.x_t/.stl/.csv —
 * that are not drawings at all). This list is a product judgement about which
 * file types are meaningful as a *drawing*, used only to shape the native
 * file picker's `accept` attribute — a client-side UX convenience, not a
 * validation boundary.
 *
 * The server (`validateUpload` in
 * `apps/api/src/modules/intake/upload-policy.ts`) deliberately validates a
 * broader, purpose-agnostic MIME allowlist shared by CAD and drawing uploads
 * alike, and in particular accepts `application/octet-stream` — the fallback
 * browsers report when they can't sniff a type, which in practice is what
 * carries most `.dwg`/`.dxf` uploads. So this list is NOT derived from, and
 * need not stay consistent with, the server's allowlist; narrowing or
 * widening it is a product-only decision that doesn't change what the server
 * accepts.
 */
export const ACCEPTED_DRAWING_EXTENSIONS = ['.pdf', '.dwg', '.dxf'] as const
